import footerCowUrl from "../../img/themes/tine/footer_cow.svg";
import footerGrassUrl from "../../img/themes/tine/footer_grass.svg";
import desktopLogoUrl from "../../img/themes/tine/logo_blue.svg";
import mobileLogoUrl from "../../img/themes/tine/logo_white.svg";
import { apiClient } from "../common/apiClient";
import { CONTENTFUL_ENV, ENV_NAME } from "../common/environment";
import { platformRoutes, resolvePlatformRequestUrl } from "../common/platformRoutes";
import type { CustomerFeature } from "../common/types/featureTypes";
import type { Invoice } from "../common/types/invoiceTypes";
import type { SupportedLocaleType } from "../common/types/languageTypes";
import { ORDERTYPE } from "../common/types/productOrderTypes";
import type { SiteConfig } from "../common/types/themeTypes";

const ALGOLIA_APPID = "4RRKWA9QAI";
const algoliaEnvironment = ENV_NAME === "prod" ? "prod" : "preprod";

const tineSiteConfig: SiteConfig = {
   storeId: "tinehandel",
   storeCompanyName: "TINE",
   orderSupportEmail: "kundeservice@tine.no",
   siteName: "TINE Handel",
   m3CompanyNumber: "100",
   m3DivisionNumber: "102",
   defaultOrderType: ORDERTYPE.WEB,
   footerDecorationImageUrl1: footerGrassUrl,
   footerDecorationImageUrl2: footerCowUrl,
   mainMenuProductCategoryLevel: 0,
   showComparisonPrice: true,
   tipApiPrefix: "",
   storeLogo: {
      searchbar: {
         url: desktopLogoUrl,
         width: 50
      },
      searchbarMobile: {
         url: mobileLogoUrl,
         width: 40
      },
      footer: {
         url: mobileLogoUrl,
         width: 72,
         mobileWidth: 50
      }
   },
   faqSlug: "/kundeservice/ofte-stilte-sporsmal",
   baseUrl: "https://www.tinehandel.no",
   filterAttributes: (variant) => {
      const isLiquid =
         /melk/i.test(variant) ||
         /flote/i.test(variant) ||
         /juice/i.test(variant) ||
         /iste/i.test(variant) ||
         /mineral/i.test(variant);
      const isSolid =
         /ost/i.test(variant) ||
         /yoghurt/i.test(variant) ||
         /smor/i.test(variant) ||
         /romme/i.test(variant) ||
         /cottage/i.test(variant) ||
         /grot/i.test(variant) ||
         /ferdig/i.test(variant) ||
         /creme/i.test(variant);

      const measurementDependentFilters = isLiquid
         ? { volumerange: "Volum" }
         : isSolid
           ? { weightrange: "Vekt" }
           : { sizerange: "Størrelse" };

      return {
         tastes: "Smak",
         tastegroups: "Smak",
         allergyinfo: "Fri for",
         certifications: "Sertifisering",
         ...measurementDependentFilters
      };
   },
   isLegacyFilters: false,
   papirflyApsCustomerId: "12590",
   papirflyApsImagePrefix: "",
   fetchCustomerFeatures: async (customerNumber: string, sessionToken: string) => {
      const url = resolvePlatformRequestUrl(platformRoutes.customerFeatureAvailability, { customerNumber });
      return (await apiClient(url, sessionToken).get().json()) as CustomerFeature[];
   },
   invoiceTooltipText: (invoice: Invoice) =>
      `Intrum saksnr. er:${invoice.debtCollectionCaseNumber}. Kontaktes på Inkasso telefonnr.:+4723211000`,

   deliveryFee: {
      enabled: true
   },
   productAvailability: {
      enabled: true
   },
   newCustomerForm: "https://kundeskjema.tine.no/",
   deliveryNotes: {
      visible: true
   },
   deliveryMethod: "inhouse",
   searchConfig: {
      algoliaAppId: ALGOLIA_APPID,
      categorySearchIndexName: `products_${algoliaEnvironment}_customer_groups`,
      explainerSearchIndexName: `contentful_${CONTENTFUL_ENV}_explainers`,
      searchDomains: [
         {
            type: "product",
            name: "produkter",
            displayName: (locale: SupportedLocaleType) => (locale === "en-US" ? "Products" : "Produkter"),
            homePath: (_locale) => "/produkter.html",
            indexName: `products_${algoliaEnvironment}_customer_groups`,
            linkText: (_locale) => "Se alle produkter"
         },
         {
            type: "v2_article",
            name: "inspirasjon",
            displayName: (locale: SupportedLocaleType) => (locale === "en-US" ? "Inspiration" : "Matfaglig inspirasjon"),
            homePath: () => "/matfaglig",
            indexName: `contentful_${CONTENTFUL_ENV}`,
            linkText: () => "Se all matfaglig inspirasjon"
         },
         {
            type: "recipe",
            name: "oppskrifter",
            displayName: (locale: SupportedLocaleType) => (locale === "en-US" ? "Recipes" : "Oppskrifter"),
            homePath: (_locale) => "/oppskrifter",
            indexName: `contentful_${CONTENTFUL_ENV}`,
            linkText: () => "Se alle oppskrifter"
         }
      ]
   },
   contentImages: {
      cloudinaryMapping: "content_images"
   },
   contactForm: {
      reasons: [
         { value: "delivery", label: "Spørsmål angående levering", label_en: "Questions about ordering" },
         { value: "invoice", label: "Endring av fakturaadresse", label_en: "Invoice address changes" },
         { value: "invoice_credit", label: "Spørsmål angående faktura", label_en: "Invoice inquiries" },
         { value: "other", label: "Annet", label_en: "Other" },
         { value: "product_order", label: "Spørsmål angående bestillling", label_en: "Questions about ordering" },
         { value: "product_question", label: "Spørsmål angående produkter", label_en: "Question about products" },
         {
            value: "refrigeration_equipment",
            label: "Spørsmål angående kjølemøbler",
            label_en: "Questions about refrigeration equipment"
         }
      ]
   },
   whileYouWaitMessage:
      "Ta frem en Go'morgen yoghurt fra kjøleskapet, len deg tilbake og nyt et par sekunders velfortjent pause!",
   notifications: {
      categories: [
         { id: "ORDER_CONFIRMATION", name: "Ordrebekreftelse" },
         { id: "DELIVERY_CONFIRMATION", name: "Leveringsbekreftelse" },
         { id: "IMPORTANT_INFO_ABOUT_DELIVERY", name: "Viktig melding om levering" },
         { id: "ORDER_DEADLINE", name: "Påminnelse om ordrefrist" }
      ],
      enableDeliveryMessages: true
   },
   orderConfirmation: {
      headerSubTitle: "Du vil motta en bestillingsbekreftelse på e-post om kort tid."
   },
   cart: {
      showReferencesByDefault: false,
      customerReferenceLabel: "Betalingsreferanse"
   },
   promotionsPage: {
      enabled: true
   },
   allowFreeProducts: false
};

export default tineSiteConfig;
